import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BottomSheet } from '@/components/base/BottomSheet';
import { showToast } from '@/components/base/Toast';
import { getFarmerById, getFarmerKyc, getSeasonsByFarmer, getContractsByFarmer, getLimitsByFarmer, getFarmerStk, getFieldsByFarmer, submitLoan } from '@/store';
import { useAuth } from '@/hooks/useAuth';
import { useStore } from '@/hooks/useStore';
import { useCategories } from '@/hooks/useCategories';
import { formatCurrencyCompact, formatCurrencyFull } from '@/utils/formatters';
import { listFinancingProducts, type BackendFinancingProduct } from '@/api/lending';
import { MATERIAL_GROUP_FALLBACK, UNIT_OF_MEASURE_FALLBACK } from '@/constants/categoryFallbacks';
import type { FarmerKycInfo } from '@/store';

interface ApplyLoanSheetProps {
  open: boolean;
  onClose: () => void;
  farmerId: string;
  kyc: FarmerKycInfo | null;
}

// The 3 counted steps of the wizard — the eKYC gate happens before 'form'
// and isn't counted in the "Bước X trên 3" indicator.
type WizardStep = 'form' | 'review' | 'terms';
type Step = 'kyc_check' | WizardStep | 'done';

const WIZARD_STEPS: WizardStep[] = ['form', 'review', 'terms'];

// Quick-pick amount presets shown as chips in Step 1 (VND).
const AMOUNT_PRESETS = [10_000_000, 20_000_000, 30_000_000, 50_000_000];

const digitsOnly = (value: string) => value.replace(/\D/g, '');
const formatMoneyInput = (value: string) => digitsOnly(value).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const parseMoneyInput = (value: string) => Number(digitsOnly(value) || 0);

function StepProgress({ step }: { step: WizardStep }) {
  const idx = WIZARD_STEPS.indexOf(step);
  const labels: Record<WizardStep, string> = {
    form: 'Thông tin vay',
    review: 'Xem lại',
    terms: 'Xác nhận',
  };
  return (
    <div className="mb-4">
      <p className="text-center text-[11px] font-medium text-gray-400 mb-2">Bước {idx + 1} trên {WIZARD_STEPS.length} · {labels[step]}</p>
      <div className="flex items-center gap-1.5">
        {WIZARD_STEPS.map((s, i) => (
          <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= idx ? 'bg-agrida-600' : 'bg-gray-200'}`} />
        ))}
      </div>
    </div>
  );
}

export function ApplyLoanSheet({ open, onClose, farmerId, kyc }: ApplyLoanSheetProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('kyc_check');
  const [materialsOnly, setMaterialsOnly] = useState(false);
  const [loai, setLoai] = useState<'Tiền' | 'Vật tư'>('Tiền');
  const [seasonId, setSeasonId] = useState('');
  const [contractId, setContractId] = useState('');
  const [financingProducts, setFinancingProducts] = useState<BackendFinancingProduct[]>([]);
  const [financingProductId, setFinancingProductId] = useState('');
  const [loanAmount, setLoanAmount] = useState('');
  const [customAmount, setCustomAmount] = useState(false);
  const [loanPurpose, setLoanPurpose] = useState('');
  const { names: materialGroups } = useCategories('MATERIAL_GROUP', MATERIAL_GROUP_FALLBACK);
  const { names: unitOptions } = useCategories('UNIT_OF_MEASURE', UNIT_OF_MEASURE_FALLBACK);
  const [nhomVatTu, setNhomVatTu] = useState('');
  const [tenVatTu, setTenVatTu] = useState('');
  const [soLuongVatTu, setSoLuongVatTu] = useState('');
  const [donViVatTu, setDonViVatTu] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // farmer, seasons, limits, contracts, fields and the bank account all come from
  // KeyedAsyncCache: the first read returns a fallback and starts a fetch, then
  // notify() fires when it lands. Nothing here subscribed, so on a fresh session
  // the sheet kept the fallbacks — an empty contract dropdown and a zero limit —
  // until the user had visited the pages that warm those caches and come back.
  // Called for the subscription only, same as HtxFarmerPage does.
  useStore();

  // The store reads below run on every render instead of through useMemo. Keyed on
  // farmerId alone the memos pinned that first empty result for the life of the
  // sheet, and the store snapshot cannot be added as a dep without tripping
  // react-hooks/exhaustive-deps since the callbacks never mention it. Each list is
  // a handful of rows per farmer. Checked first that no useEffect depends on any
  // of them, so the fresh array identities cannot drive a render loop.
  const farmer = getFarmerById(farmerId);
  const isKycVerified = kyc?.kycStatus === 'ekyc_verified';

  // Only show active/valid seasons
  const seasons = getSeasonsByFarmer(farmerId).filter((s) =>
    s.status === 'Đang canh tác' || s.status === 'Chuẩn bị' || s.status === 'Sắp thu hoạch',
  );

  const moneyLimits = getLimitsByFarmer(farmerId).filter((l) => l.loaiDinhMuc === 'Vay vốn sản xuất');
  const hanMucTienConLai = useMemo(() => {
    const total = moneyLimits.reduce((s, l) => s + l.hanMuc, 0);
    const used = moneyLimits.reduce((s, l) => s + l.daSuDung, 0);
    return Math.max(0, total - used);
  }, [moneyLimits]);
  const stk = getFarmerStk(farmerId);

  useEffect(() => {
    if (!nhomVatTu && materialGroups.length > 0) setNhomVatTu(materialGroups[0]);
  }, [materialGroups, nhomVatTu]);

  useEffect(() => {
    listFinancingProducts()
      .then((products) => {
        setFinancingProducts(products);
        setFinancingProductId((current) => current || products[0]?._id || '');
      })
      .catch(() => setFinancingProducts([]));
  }, []);

  // Active contracts for dropdown
  const activeContracts = getContractsByFarmer(farmerId).filter((c) =>
    c.loaiHopDong === 'HTX-Nông hộ' &&
    (c.status === 'Đang hiệu lực' || c.status === 'Sắp hết hạn'),
  );

  const selectedContract = useMemo(() =>
    contractId ? activeContracts.find((c) => c.id === contractId) : null,
    [contractId, activeContracts]
  );
  const contractSeasons = useMemo(() => {
    if (loai === 'Vật tư') return seasons;
    if (!selectedContract) return [];
    const allowedIds = new Set(selectedContract.seasonIds || []);
    return seasons.filter((season) => allowedIds.has(season.id));
  }, [selectedContract, seasons, loai]);
  // What the farmer may still borrow is their own remaining limit, full stop.
  //
  // This used to be min(remaining limit, contract.hanMucConLai), and
  // hanMucConLai maps to the backend's limitRemainingVnd — which
  // ContractService.grantLimitFromContract sets to `valueVnd - limitGrantedVnd`,
  // i.e. how much MORE limit could still be granted out of this contract. That is
  // not a borrowing cap, and using it as one is what made the loan sheet contradict
  // the limit page: a 35tr contract with 20tr granted left 15tr of grant headroom,
  // so the sheet advertised 15tr while the farmer's limit page correctly showed the
  // limit's own remaining balance.
  //
  // There is deliberately no per-contract borrowing cap: LimitService keeps exactly
  // one running limit per farmer and pools every grant into it (see the note in
  // store/limits.ts), so per-contract usage simply is not tracked anywhere. The
  // dead `contractLimit` lookup that used to sit here could never match either —
  // toFrontendLimit never populates `contractId`.
  const availableByContract = selectedContract ? hanMucTienConLai : 0;
  const contractGranted = selectedContract?.hanMucDaCap || 0;

  const contractWarning = Boolean(selectedContract?.expiringSoon) || selectedContract?.status === 'Sắp hết hạn';
  const hasExpiredContracts = getContractsByFarmer(farmerId).some((c) =>
    c.loaiHopDong === 'HTX-Nông hộ' && c.status === 'Hết hạn',
  );

  const selectedSeason = useMemo(() => seasons.find((s) => s.id === seasonId), [seasons, seasonId]);
  const selectedField = selectedSeason ? getFieldsByFarmer(farmerId).find((item) => item.id === selectedSeason.fieldId) : undefined;
  const amountNum = parseMoneyInput(loanAmount);

  const canContinueFromForm = loai === 'Tiền'
    ? Boolean(financingProductId && contractId && seasonId && loanAmount && amountNum > 0 && amountNum <= availableByContract)
    : Boolean(seasonId && tenVatTu.trim() && loanPurpose.trim());

  const resetAndClose = () => {
    setStep('kyc_check');
    setMaterialsOnly(false);
    setLoai('Tiền');
    setSeasonId('');
    setContractId('');
    setFinancingProductId(financingProducts[0]?._id ?? '');
    setLoanAmount('');
    setCustomAmount(false);
    setLoanPurpose('');
    setNhomVatTu(materialGroups[0] ?? '');
    setTenVatTu('');
    setSoLuongVatTu('');
    setDonViVatTu('');
    setAgreeTerms(false);
    setSubmitting(false);
    onClose();
  };

  const handleClose = () => resetAndClose();

  const goToFormAsMaterialsOnly = () => {
    setMaterialsOnly(true);
    setLoai('Vật tư');
    setStep('form');
  };

  const handleSubmit = () => {
    if (!agreeTerms) {
      showToast('Vui lòng đồng ý với điều khoản trước khi gửi yêu cầu', 'warning');
      return;
    }
    if (loai === 'Tiền') {
      if (!isKycVerified) {
        showToast('Bạn cần xác thực eKYC trước khi vay tiền', 'warning');
        return;
      }
      if (!loanAmount || amountNum <= 0) {
        showToast('Số tiền không hợp lệ', 'warning');
        return;
      }
      if (!contractId || !selectedContract) {
        showToast('Vui lòng chọn hợp đồng bao tiêu liên quan trước khi vay.', 'warning');
        return;
      }
      if (!financingProductId) {
        showToast('Vui lòng chọn sản phẩm tài chính trước khi vay.', 'warning');
        return;
      }
      if (amountNum > availableByContract) {
        showToast(`Số tiền vượt hạn mức còn lại của hợp đồng (${formatCurrencyCompact(availableByContract)}).`, 'warning');
        return;
      }
    } else if (!tenVatTu.trim()) {
      showToast('Vui lòng nhập tên vật tư', 'warning');
      return;
    }
    if (!seasonId) {
      showToast('Vui lòng chọn mùa vụ để HTX xét duyệt khoản vay.', 'warning');
      return;
    }
    // No user.htxId gate here any more. That field is only set at registration and
    // is never updated when a farmer joins or is moved between cooperatives, so a
    // farmer who self-registered and then joined an HTX was blocked outright with
    // "Không xác định được hợp tác xã của tài khoản" — even with a valid contract,
    // a season and an amount well inside their limit. LoanService now reads the
    // cooperative off the farmer's own profile.
    setSubmitting(true);
    submitLoan({
      farmerId,
      loai,
      seasonId: seasonId || undefined,
      financingProductId: loai === 'Tiền' ? financingProductId : undefined,
      soTien: loai === 'Tiền' ? amountNum : 0,
      mucDich: loanPurpose || (loai === 'Tiền' ? 'Vay tiền sản xuất' : 'Vay vật tư sản xuất'),
      tenVatTu: loai === 'Vật tư' ? tenVatTu.trim() : undefined,
      soLuongVatTu: loai === 'Vật tư' ? (Number(soLuongVatTu) || undefined) : undefined,
      donViVatTu: loai === 'Vật tư' ? (donViVatTu.trim() || undefined) : undefined,
      nhomVatTu: loai === 'Vật tư' ? nhomVatTu : undefined,
    })
      .then(() => {
        setStep('done');
        setSubmitting(false);
      })
      .catch(() => {
        showToast('Không thể gửi yêu cầu, vui lòng thử lại', 'error');
        setSubmitting(false);
      });
  };

  return (
    <BottomSheet open={open} onClose={handleClose} title={step === 'done' ? 'Đã gửi yêu cầu' : 'Xin vay'}>
      {step !== 'kyc_check' && step !== 'done' && <StepProgress step={step} />}

      {step === 'kyc_check' && (
        <div className="space-y-4">
          {isKycVerified ? (
            <>
              <div className="bg-agrida-50 border border-agrida-200 rounded-xl p-4 text-center">
                <span className="w-14 h-14 rounded-full bg-agrida-100 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-shield-check-line text-agrida-600 text-2xl" />
                </span>
                <p className="text-sm font-bold text-agrida-800">Tài khoản đã xác thực</p>
                <p className="text-[11px] text-agrida-600 mt-1">Bạn có thể tiếp tục gửi yêu cầu vay</p>
              </div>
              <button
                type="button"
                onClick={() => { setMaterialsOnly(false); setStep('form'); }}
                className="w-full px-4 py-3 rounded-xl text-sm font-bold bg-agrida-600 text-white hover:bg-agrida-700 transition-colors whitespace-nowrap min-h-[48px]"
              >
                Tiếp tục nhập thông tin vay
              </button>
            </>
          ) : (
            <div className="space-y-4 text-center">
              <span className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto">
                <i className="ri-lock-line text-amber-600 text-2xl" />
              </span>
              <div>
                <p className="text-sm font-bold text-gray-900 mb-1">Cần xác thực eKYC</p>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Tính năng vay tiền yêu cầu tài khoản đã được xác thực danh tính. Bạn cần hoàn tất eKYC trước khi gửi yêu cầu vay tiền.
                </p>
              </div>
              <button
                type="button"
                onClick={() => { onClose(); navigate('/ekyc'); }}
                className="w-full px-4 py-3 rounded-xl text-sm font-bold bg-agrida-600 text-white hover:bg-agrida-700 transition-colors whitespace-nowrap min-h-[48px]"
              >
                Thực hiện eKYC ngay
              </button>
              <button
                type="button"
                onClick={goToFormAsMaterialsOnly}
                className="w-full text-xs font-medium text-gray-500 hover:text-agrida-700 underline underline-offset-2"
              >
                Chỉ cần vật tư? Gửi yêu cầu vay vật tư (không cần eKYC)
              </button>
            </div>
          )}
        </div>
      )}

      {step === 'form' && (
        <div className="space-y-3">
          {!materialsOnly && (
            <div className="flex gap-2 p-1 bg-gray-100 rounded-xl">
              <button
                type="button"
                onClick={() => setLoai('Tiền')}
                className={`flex-1 py-2 rounded-xl text-xs font-bold transition-colors ${loai === 'Tiền' ? 'bg-white text-agrida-700 shadow-sm' : 'text-gray-500'}`}
              >
                Vay tiền
              </button>
              <button
                type="button"
                onClick={() => setLoai('Vật tư')}
                className={`flex-1 py-2 rounded-xl text-xs font-bold transition-colors ${loai === 'Vật tư' ? 'bg-white text-agrida-700 shadow-sm' : 'text-gray-500'}`}
              >
                Vay vật tư
              </button>
            </div>
          )}

          {loai === 'Tiền' && (
            <div className="bg-gray-50 rounded-xl p-3 grid grid-cols-2 gap-2">
              <div>
                {/* Same figure as "Còn khả dụng" on the Hạn mức page — both read the
                    farmer's one running limit, so they cannot disagree any more. */}
                <p className="text-[10px] text-gray-400 mb-0.5">Hạn mức còn khả dụng</p>
                <p className="text-sm font-bold text-agrida-700">{formatCurrencyCompact(availableByContract)}</p>
                {contractGranted > 0 && (
                  <p className="text-[10px] text-gray-400 mt-0.5">Hợp đồng đã cấp {formatCurrencyCompact(contractGranted)}</p>
                )}
              </div>
              {stk && (
                <div>
                  <p className="text-[10px] text-gray-400 mb-0.5">Tài khoản nhận</p>
                  <p className="text-xs font-medium text-gray-700">{stk.nganHang}</p>
                  <p className="text-[10px] text-gray-500">{stk.stkMask} - {stk.chuTaiKhoan}</p>
                </div>
              )}
            </div>
          )}

          {loai === 'Tiền' && (
            <div>
              <label className="text-[11px] font-medium text-gray-500 mb-1 block">Hợp đồng liên quan</label>
              <select
                value={contractId}
                onChange={(e) => { setContractId(e.target.value); setSeasonId(''); }}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-xs text-gray-900 bg-white focus:outline-none focus:border-agrida-500"
              >
                <option value="">-- Chọn hợp đồng --</option>
                {activeContracts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.soHopDong} ({c.expiringSoon || c.status === 'Sắp hết hạn' ? 'Sắp HH' : 'Hiệu lực'})
                  </option>
                ))}
              </select>
              {contractWarning && (
                <p className="text-[10px] text-amber-600 mt-1 flex items-center gap-1">
                  <i className="ri-error-warning-line" /> Hợp đồng sắp hết hạn. Cân nhắc gia hạn.
                </p>
              )}
              {activeContracts.length === 0 && hasExpiredContracts && (
                <p className="text-[10px] text-red-500 mt-1 flex items-center gap-1">
                  <i className="ri-close-circle-line" /> Hợp đồng đã hết hạn. Liên hệ HTX để gia hạn.
                </p>
              )}
              {activeContracts.length === 0 && !hasExpiredContracts && (
                <p className="text-[10px] text-amber-500 mt-1">Chưa có hợp đồng bao tiêu</p>
              )}
            </div>
          )}

          {loai === 'Tiền' && (
            <div>
              <label className="text-[11px] font-medium text-gray-500 mb-1 block">Sản phẩm vay</label>
              <select
                value={financingProductId}
                onChange={(e) => setFinancingProductId(e.target.value)}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-xs text-gray-900 bg-white focus:outline-none focus:border-agrida-500"
              >
                <option value="">-- Chọn sản phẩm vay --</option>
                {financingProducts.map((p) => (
                  <option key={p._id} value={p._id}>{p.name} ({p.interestRatePercentPerYear}%/năm, {p.termMonths} tháng)</option>
                ))}
              </select>
              {financingProducts.length === 0 && (
                <p className="text-[10px] text-amber-500 mt-1">Chưa có sản phẩm vay nào đang mở</p>
              )}
            </div>
          )}

          <div>
            <label className="text-[11px] font-medium text-gray-500 mb-1 block">Mùa vụ / thửa đất{loai === 'Tiền' ? ' trong hợp đồng' : ''}</label>
            <select
              value={seasonId}
              onChange={(e) => setSeasonId(e.target.value)}
              disabled={loai === 'Tiền' && !selectedContract}
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-xs text-gray-900 bg-white focus:outline-none focus:border-agrida-500 disabled:bg-gray-50 disabled:text-gray-400"
            >
              <option value="">{loai === 'Tiền' && !selectedContract ? '-- Chọn hợp đồng trước --' : '-- Chọn mùa vụ / thửa đất --'}</option>
              {contractSeasons.map((s) => {
                const field = getFieldsByFarmer(farmerId).find((item) => item.id === s.fieldId);
                return <option key={s.id} value={s.id}>{s.name} · {field?.name || 'Thửa đất'} ({s.cayTrong} {s.variety})</option>;
              })}
            </select>
            {/* A farmer cannot fix this themselves — only the HTX/back-office links a
                season to a contract — and the field is required, so without saying who
                to ask the Tiếp tục button just sits there disabled with no way out. */}
            {loai === 'Tiền' && selectedContract && contractSeasons.length === 0 && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 mt-1.5 flex items-start gap-1.5" role="alert">
                <i className="ri-error-warning-line mt-0.5 shrink-0" />
                <span>
                  Hợp đồng này chưa gắn mùa vụ/thửa đất nên chưa thể gửi yêu cầu vay tiền.
                  Vui lòng đề nghị hợp tác xã gắn mùa vụ vào hợp đồng, hoặc chọn hình thức <strong>Vay vật tư</strong>.
                </span>
              </p>
            )}
          </div>

          {loai === 'Tiền' ? (
            <div>
              <label className="text-[11px] font-medium text-gray-500 mb-1 block">Số tiền đề nghị (VNĐ)</label>
              <div className="grid grid-cols-4 gap-1.5">
                {AMOUNT_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => { setLoanAmount(formatMoneyInput(String(preset))); setCustomAmount(false); }}
                    className={`py-2.5 rounded-xl text-xs font-bold transition-colors whitespace-nowrap ${
                      !customAmount && amountNum === preset ? 'bg-agrida-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {preset / 1_000_000} triệu
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setCustomAmount((v) => !v)}
                className="mt-1.5 text-[11px] font-medium text-agrida-700 underline underline-offset-2"
              >
                {customAmount ? 'Ẩn nhập số tiền khác' : '+ Số tiền khác'}
              </button>
              {customAmount && (
                <input
                  type="text"
                  inputMode="numeric"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(formatMoneyInput(e.target.value))}
                  placeholder="VD: 10.000.000"
                  autoFocus
                  className="mt-2 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-xs text-gray-900 bg-white focus:outline-none focus:border-agrida-500"
                />
              )}
              {loanAmount && (
                <p className="mt-1.5 text-[10px] italic text-gray-400">{formatCurrencyFull(amountNum)}</p>
              )}
              {amountNum > availableByContract && (
                <p className="text-[10px] text-red-500 mt-1 flex items-center gap-1">
                  <i className="ri-error-warning-line" /> Vượt hạn mức còn khả dụng {formatCurrencyCompact(availableByContract)}. Không thể gửi yêu cầu.
                </p>
              )}
            </div>
          ) : (
            <>
              <div>
                <label className="text-[11px] font-medium text-gray-500 mb-1 block">Nhóm vật tư</label>
                <select
                  value={nhomVatTu}
                  onChange={(e) => setNhomVatTu(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-xs text-gray-900 bg-white focus:outline-none focus:border-agrida-500"
                >
                  {materialGroups.map((n) => (<option key={n} value={n}>{n}</option>))}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-medium text-gray-500 mb-1 block">Tên vật tư</label>
                <input type="text" value={tenVatTu} onChange={(e) => setTenVatTu(e.target.value)} placeholder="Tên vật tư cụ thể" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-xs text-gray-900 bg-white focus:outline-none focus:border-agrida-500" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[11px] font-medium text-gray-500 mb-1 block">Số lượng</label>
                  <input type="number" value={soLuongVatTu} onChange={(e) => setSoLuongVatTu(e.target.value)} placeholder="Số lượng" className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-xs text-gray-900 bg-white focus:outline-none focus:border-agrida-500" />
                </div>
                <div>
                  <label className="text-[11px] font-medium text-gray-500 mb-1 block">Đơn vị</label>
                  <input type="text" list="loan-unit-of-measure-options" value={donViVatTu} onChange={(e) => setDonViVatTu(e.target.value)} placeholder="kg, lít..." className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-xs text-gray-900 bg-white focus:outline-none focus:border-agrida-500" />
                  <datalist id="loan-unit-of-measure-options">
                    {unitOptions.map((u) => (<option key={u} value={u} />))}
                  </datalist>
                </div>
              </div>
            </>
          )}

          <div>
            <label className="text-[11px] font-medium text-gray-500 mb-1 block">Mục đích {loai === 'Tiền' ? 'vay' : 'sử dụng'}</label>
            <input
              type="text"
              value={loanPurpose}
              onChange={(e) => setLoanPurpose(e.target.value)}
              placeholder={loai === 'Tiền' ? 'VD: Mua phân bón vụ Đông Xuân' : 'VD: Cần vật tư cho vụ Đông Xuân'}
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-xs text-gray-900 bg-white focus:outline-none focus:border-agrida-500"
            />
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => setStep('kyc_check')}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              ← Quay lại
            </button>
            <button
              type="button"
              onClick={() => setStep('review')}
              disabled={!canContinueFromForm}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold bg-agrida-600 text-white hover:bg-agrida-700 disabled:bg-gray-300 disabled:text-gray-500 transition-colors whitespace-nowrap"
            >
              Tiếp tục
            </button>
          </div>
        </div>
      )}

      {step === 'review' && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-500">Kiểm tra lại thông tin trước khi gửi</p>
            <button type="button" onClick={() => setStep('form')} className="text-xs font-bold text-agrida-700 underline underline-offset-2">Sửa</button>
          </div>

          {loai === 'Tiền' ? (
            <div className="bg-agrida-50 border border-agrida-200 rounded-xl p-4 text-center">
              <p className="text-[11px] text-agrida-700 mb-1">Số tiền đề nghị vay</p>
              <p className="text-3xl sm:text-4xl font-extrabold text-agrida-800 leading-tight">{formatCurrencyCompact(amountNum)}</p>
              <p className="text-[11px] text-agrida-600 mt-0.5">{formatCurrencyFull(amountNum)}</p>
            </div>
          ) : (
            <div className="bg-agrida-50 border border-agrida-200 rounded-xl p-4 text-center">
              <p className="text-[11px] text-agrida-700 mb-1">Vật tư đề nghị</p>
              <p className="text-2xl font-extrabold text-agrida-800 leading-tight">{tenVatTu}</p>
              {(soLuongVatTu || donViVatTu) && (
                <p className="text-xs text-agrida-600 mt-0.5">{soLuongVatTu} {donViVatTu}</p>
              )}
            </div>
          )}

          <div className="bg-gray-50 rounded-xl p-3 space-y-2 text-xs">
            {loai === 'Tiền' && selectedContract && (
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Hợp đồng</span>
                <span className="font-medium text-gray-800">{selectedContract.soHopDong}</span>
              </div>
            )}
            {loai === 'Tiền' && (
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Sản phẩm vay</span>
                <span className="font-medium text-gray-800">{financingProducts.find((p) => p._id === financingProductId)?.name || '—'}</span>
              </div>
            )}
            {loai === 'Vật tư' && (
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Nhóm vật tư</span>
                <span className="font-medium text-gray-800">{nhomVatTu}</span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Mùa vụ</span>
              <span className="font-medium text-gray-800">{selectedSeason?.name || '—'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Thửa đất</span>
              <span className="font-medium text-gray-800">{selectedField?.name || '—'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Mục đích</span>
              <span className="font-medium text-gray-800 text-right max-w-[60%]">{loanPurpose || '—'}</span>
            </div>
            {loai === 'Tiền' && stk && (
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Tài khoản nhận</span>
                <span className="font-medium text-gray-800 text-right">{stk.nganHang} · {stk.stkMask}</span>
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => setStep('form')}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              ← Quay lại
            </button>
            <button
              type="button"
              onClick={() => setStep('terms')}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold bg-agrida-600 text-white hover:bg-agrida-700 transition-colors whitespace-nowrap"
            >
              Tiếp tục
            </button>
          </div>
        </div>
      )}

      {step === 'terms' && (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-xl p-3">
            <label className="flex items-start gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={agreeTerms}
                onChange={(e) => setAgreeTerms(e.target.checked)}
                className="mt-0.5 w-4 h-4 rounded border-gray-300 text-agrida-600 focus:ring-agrida-500"
              />
              <span className="text-xs text-gray-600 leading-relaxed">
                Tôi xác nhận thông tin đề nghị {loai === 'Tiền' ? 'vay tiền' : 'vay vật tư'} ở trên là chính xác và đồng ý để HTX xem xét, phê duyệt theo quy định.
              </span>
            </label>
          </div>

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => setStep('review')}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              ← Quay lại
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || !agreeTerms}
              className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold bg-agrida-600 text-white hover:bg-agrida-700 disabled:bg-gray-300 disabled:text-gray-500 transition-colors whitespace-nowrap"
            >
              {submitting ? 'Đang gửi...' : 'Gửi yêu cầu'}
            </button>
          </div>
        </div>
      )}

      {step === 'done' && (
        <div className="space-y-4 text-center py-2">
          <span className="w-16 h-16 rounded-full bg-agrida-100 flex items-center justify-center mx-auto">
            <i className="ri-checkbox-circle-fill text-agrida-600 text-3xl" />
          </span>
          <div>
            <p className="text-base font-bold text-gray-900 mb-1">Đã gửi yêu cầu vay {loai === 'Tiền' ? 'tiền' : 'vật tư'}!</p>
            <p className="text-xs text-gray-500 leading-relaxed">HTX sẽ xem xét và phản hồi sớm. Bạn có thể theo dõi trạng thái trong mục Vay vốn.</p>
          </div>
          <button
            type="button"
            onClick={resetAndClose}
            className="w-full px-4 py-3 rounded-xl text-sm font-bold bg-agrida-600 text-white hover:bg-agrida-700 transition-colors whitespace-nowrap min-h-[48px]"
          >
            Xong
          </button>
        </div>
      )}
    </BottomSheet>
  );
}
